import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import CardHeader from '@material-ui/core/CardHeader';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';
import CommentIcon from '@material-ui/icons/Comment';

import RedButton from 'app/components/buttons/RedButton';
import Loading from 'app/components/loaders/Loading';
import SearchUsersAutoComplete from 'app/components/user/UserSearchAutocomplete';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    titleCloseButton: {
        paddingRight: theme.spacing(8)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    content: {
        padding: theme.spacing(3)
    },
    dialogActions: {
        justifyContent: 'space-between'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    listItem: {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: '#ffffff',
        boxShadow: theme.shadows[4],
        marginBottom: theme.spacing(3)
    },
    link: {
        cursor: 'pointer'
    }
}))

export default function EditRecommendationDialog(props) {

    // Construct -----------------------------

    const {
        election,
        proposal,
        recommendation,
        open,
        onClose,
        onSave,
        onDelete,
        onClosed,
        admin,
    } = props

    const api = useApi();
    const { app } = useApp();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ user, setUser ] = useState({id: 0})
    const [ subject, setSubject ] = useState("");
    const [ content, setContent ] = useState("");
    const [ templates, setTemplates ] = useState([]);
    const [ step, setStep ] = useState(1);
    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ templatesLoading, setTemplatesLoading ] = useState(false);

    const getTemplates = () => {
        setTemplatesLoading(true);

        api.get(`/templates/recommendation`)
            .then((res) => setTemplates(res.templates))
            .finally(() => setTemplatesLoading(false))
    }

    const onOpen = () => {

        setContent(recommendation ? recommendation.content : "");
        setSubject("");
        setStep(1);
        setErrors({});
        setUser((recommendation && recommendation.user) ? recommendation.user : {id: 0});

        if (election.recommendations_canned_responses) {
            getTemplates();
        }
    };

    // Handlers -------------------------------------------------

    const handleContent = event => {

        const newContent = event.target.value;

        if (isError('content')) delete errors.content;

        if (newContent.length > proposal.recommendations_character_limit) {
            setErrors({ 'content': 'Too many Characters'})
        }

        setContent(newContent);
    };

    const handleSubject = event => {

        const newSubject = event.target.value;

        if (isError('subject')) delete errors.subject;

        if (newSubject.length > 78) {
            setErrors({ 'subject': 'Too many Characters'})
        }

        setSubject(newSubject);
    };

    const handleClose = (e, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
            e.preventDefault();
            return false;
        }

        onClose();
    }

    const useTemplate = template => {
        setContent(template.content);
        setStep(1);
    }

    const handleUser = user => {
        if (isError('user_id')) delete errors.user_id;

        setUser(user);
    }

    // Posters ---------------------------------

    const postRecommendation = () => {

        setLoading(true);

        const url = admin ? '/admin' : '';

        const data = {
            id: recommendation?.id,
            user_id: user.id,
            content: content,
        }

        api.post(`${url}/election/${election.id}/proposal/${proposal.id}/recommendation`, data)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false))
    }

    const postTemplate = () => {

        setLoading(true);

        const data = {
            subject: subject,
            content: content,
        }

        api.post(`/templates/recommendation`, data)
            .then(() => {
                setStep(1);
                if (election.recommendations_canned_responses) getTemplates();
            }).catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false))
    }

    const deleteRecommendation = () => {

        setLoading(true);

        const url = admin ? '/admin' : '';
        const rid = (recommendation && admin) ? `/${recommendation.id}` : '';

        api.delete(`${url}/election/proposal/${proposal.id}/recommendation${rid}`)
            .then(() => onDelete())
            .finally(() => setLoading(false))
    }

    // Error Handling

    const isError = field => {
        if (errors[field]) return true;
        return false;
    };

    const getContentMessage = field => {
        if (isError(field)) return errors[field];
        return `${(election.recommendations_character_limit - content.length)} / ${election.recommendations_character_limit} characters left.`;
    };

    const getSubjectMessage = field => {
        if (isError(field)) return errors[field];
        return `${(78 - subject.length)} / 78 characters left.`;
    };

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            fullScreen={isMobile}
            onEnter={onOpen}
            onExited={onClosed}
            maxWidth="md"
            aria-labelledby="edit-recommendation-dialog-title"
            >
            <Loading isLoading={loading}/>
            <DialogTitle
                id="edit-recommendation-dialog-title"
                className={classes.titleCloseButton}
                >
                {recommendation ? 'Edit Recommendation' : "New Recommendation"} for {proposal?.title}
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    disabled={loading}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {(step === 1) &&
                    <>
                        {admin &&
                            <div className={classes.marginBottom}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Recommendation Written By:
                                </Typography>
                                {(user.id > 0) ? (
                                    <CardHeader
                                        avatar={
                                            <Avatar
                                                aria-label={user.full_name}
                                                alt={user.full_name}
                                                className={classes.avatar}
                                                src={user.avatar ? user.avatar.full_url : null}
                                                >
                                                {initials(user)}
                                            </Avatar>
                                        }
                                        title={user.full_name}
                                        subheader={user.membership_title}
                                        action={
                                            <IconButton
                                                aria-label="Remove User"
                                                onClick={() => handleUser({id:0})}
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        }
                                    />
                                ) : (
                                    <SearchUsersAutoComplete
                                        closeOnSelect
                                        setOnSelect
                                        onSelect={handleUser}
                                        label="Search Users..."
                                        admin={admin}
                                    />
                                )}
                            </div>
                        }
                        <Typography variant="subtitle1" gutterBottom>
                            {election.recommendations_text}<br/>
                            <Tooltip
                                title={election.recommendations_canned_responses ? 'Use a saved template.' : 'This election does not allow templates to be used.'}
                                >
                                <span>
                                    <Link
                                        color="primary"
                                        disabled={loading || election.recommendations_canned_responses}
                                        onClick={() => setStep(4)}
                                        className={classes.link}
                                        >
                                        Or click here to use a template.
                                    </Link>
                                </span>
                            </Tooltip>
                        </Typography>
                        <TextField
                            id="input-recommendation-content"
                            label="Your Recommendation"
                            name="content"
                            value={content}
                            onChange={handleContent}
                            fullWidth
                            error={isError('content')}
                            helperText={getContentMessage()}
                            variant="outlined"
                            multiline
                            autoFocus
                            margin="normal"
                            rows="6"
                            rowsMax="6"
                            inputProps={{
                                maxLength: election.recommendations_character_limit
                            }}
                        />
                        <Typography variant="subtitle1">
                            Click <strong>{recommendation ? 'Update Recommendation' : 'Submit Recommendation'}</strong> to submit your recommendation for review.
                        </Typography>
                    </>
                }
                {(step === 2) &&
                    <>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            You are about to <strong>DELETE</strong> your recommendation for <strong>{proposal?.title}</strong>.
                        </Typography>
                        <Typography variant="subtitle1" color="error" gutterBottom>
                            Are you sure you want to delete this recommendation?
                        </Typography>
                    </>
                }
                {(step === 3) &&
                    <>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Save your recommendation as a template for future usage.
                        </Typography>
                        <TextField
                            id="input-recommendation-subject"
                            label="Template Subject"
                            name="subject"
                            value={subject}
                            onChange={handleSubject}
                            fullWidth
                            error={isError('subject')}
                            helperText={getSubjectMessage()}
                            variant="outlined"
                            autoFocus
                            margin="normal"
                            inputProps={{
                                maxLength: 78
                            }}
                            className={classes.marginBottom}
                        />
                        <Typography variant="subtitle2" color="error" gutterBottom>
                            Templates are only available to use if administrators allow them for the selected election.
                        </Typography>
                    </>
                }
                {(step === 4) &&
                    <div className={classes.templatesContainer}>
                        <Loading isLoading={templatesLoading}/>
                        {(templates && templates.length > 0) ? (
                            <List>
                                {templates.map(t => (
                                    <ListItem
                                        key={t.id}
                                        button
                                        onClick={() => useTemplate(t)}
                                        className={classes.listItem}
                                        >
                                        <ListItemAvatar>
                                            <Avatar>
                                                <CommentIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={t.subject}
                                            secondary={t.short_excerpt}
                                        />
                                        {/*}
                                        <ListItemSecondaryAction>
                                                {p.recommendations.length ? (
                                                    <CheckCircleOutlineIcon style={{ color: green[600] }}/>
                                                ) : (
                                                    <CommentIcon color="action"/>
                                                )}
                                        </ListItemSecondaryAction>
                                                */}
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                You have not saved any templates.
                            </Typography>
                        )}
                    </div>
                }
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {(step === 1) &&
                    <>
                        <Tooltip title="Delete Recommendation">
                            <span>
                                <RedButton
                                    onClick={() => setStep(2)}
                                    disabled={loading || ! recommendation}
                                    >
                                    Delete
                                </RedButton>
                            </span>
                        </Tooltip>
                        <div>
                            <Tooltip title={content.length > 0 ? 'Save as template' : 'Write a recommendation to save it as a template.'}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => setStep(3)}
                                        disabled={loading || content.length === 0}
                                        className={classes.marginRight}
                                        >
                                        Save as Template
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={recommendation ? 'Update Recommendation' : 'Submit Recommendation'}>
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={postRecommendation}
                                        disabled={loading}
                                        >
                                        {recommendation ? 'Update Recommendation' : 'Submit Recommendation'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                    </>
                }
                {(step !== 1) &&
                    <>
                        <Button
                            startIcon={<LeftIcon/>}
                            aria-label="Back"
                            onClick={() => setStep(1)}
                            >
                            Back to Recommendation
                        </Button>
                        {(step === 2) &&
                            <RedButton
                                onClick={deleteRecommendation}
                                disabled={loading}
                                variant="contained"
                                >
                                Delete
                            </RedButton>
                        }
                        {(step === 3) &&
                            <Tooltip
                                title={subject.length > 0 ? 'Save Recommendation as Template' : 'Must have a Subject to save.'}
                                >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={postTemplate}
                                    disabled={loading}
                                    >
                                    Save Template
                                </Button>
                            </Tooltip>
                        }
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}
